/**
 * LISTENING PARTY SERVICE
 *
 * Firestore CRUD operations for real-time synchronized listening parties.
 * Manages:
 *   - Party creation and lifecycle (create, join, leave, end)
 *   - Real-time subscriptions to party state changes
 *   - Playback control (host-only: play, pause, seek, change track)
 *   - Member calibration tracking (latency, clock offset, drift)
 *   - Acoustic sync pulse triggering
 *
 * Data model:
 *   Collection: listeningParties/{partyId}
 *   - id: unique party ID
 *   - code: 6-char public join code (e.g., "ABC123")
 *   - hostUid: creator's user ID
 *   - memberIds: array of all member UIDs
 *   - members: { [uid]: PartyMember } map with calibration data
 *   - playback: current track + state + timing info
 *   - syncPulse: null or { at: serverTime, triggeredBy: uid }
 *   - isActive: soft-delete flag
 *
 * Firestore rules:
 *   - Any authenticated user can read parties
 *   - Members and host can update (for calibration, playback)
 *   - Host can delete (end party)
 *
 * Real-time updates:
 *   - subscribeToParty(): listens for changes via onSnapshot
 *   - When syncPulse changes, client performs acoustic sync
 *   - When playback changes, client schedules new audio output
 */

import { db } from '@/lib/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import type {
  ListeningParty,
  PartyMember,
  PartyPlayback,
} from '@/types/listening-party';

/**
 * Generate a random 6-character alphanumeric party code (e.g., "ABC123").
 */
function generatePartyCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/**
 * Create a new listening party.
 * Pass `chatId` to scope the party to a specific chat group —
 * each group can then have its own active party.
 */
export async function createParty(
  hostUid: string,
  hostName: string,
  partyName: string,
  chatId?: string,
): Promise<ListeningParty> {
  try {
    const code = generatePartyCode();
    const partyRef = doc(collection(db, 'listeningParties'));

    const party: ListeningParty = {
      id: partyRef.id,
      code,
      hostUid,
      hostName,
      name: partyName,
      memberIds: [hostUid],
      members: {
        [hostUid]: {
          uid: hostUid,
          displayName: hostName,
          audioLatencyMs: 0,
          clockOffsetMs: 0,
          syncDriftMs: 0,
          lastSeen: Date.now(),
          isMicActive: false,
        },
      },
      playback: {
        trackId: null,
        audioUrl: null,
        title: '',
        artist: '',
        imageUrl: '',
        state: 'idle',
        positionMs: 0,
        serverTimestampMs: Date.now(),
        targetEmitServerTime: Date.now(),
      },
      syncPulse: null,
      createdAt: Timestamp.now(),
      isActive: true,
      ...(chatId ? { chatId } : {}),
    };

    await setDoc(partyRef, party);
    return party;
  } catch (error) {
    console.error('Failed to create party:', error);
    throw error;
  }
}

/**
 * Find the active listening party for a specific chat room/group.
 * Returns null if no active party exists for that chat.
 */
export async function getActivePartyByChatId(chatId: string): Promise<ListeningParty | null> {
  try {
    const q = query(
      collection(db, 'listeningParties'),
      where('chatId', '==', chatId),
      where('isActive', '==', true),
      limit(1),
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return snap.docs[0].data() as ListeningParty;
  } catch {
    return null;
  }
}

/**
 * Join an existing party by code.
 */
export async function joinPartyByCode(
  code: string,
  userId: string,
  displayName: string
): Promise<ListeningParty> {
  try {
    // Find party by code
    const q = query(
      collection(db, 'listeningParties'),
      where('code', '==', code),
      where('isActive', '==', true)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      throw new Error(`No active party found with code: ${code}`);
    }

    const partyDoc = snapshot.docs[0];
    const party = partyDoc.data() as ListeningParty;

    // Add member if not already present
    if (!party.memberIds.includes(userId)) {
      party.memberIds.push(userId);
      party.members[userId] = {
        uid: userId,
        displayName,
        audioLatencyMs: 0,
        clockOffsetMs: 0,
        syncDriftMs: 0,
        lastSeen: Date.now(),
        isMicActive: false,
      };

      await updateDoc(partyDoc.ref, {
        memberIds: party.memberIds,
        members: party.members,
      });
    }

    return party;
  } catch (error) {
    console.error('Failed to join party:', error);
    throw error;
  }
}

/**
 * Leave a party.
 */
export async function leaveParty(partyId: string, userId: string): Promise<void> {
  try {
    const partyRef = doc(db, 'listeningParties', partyId);
    const partyDoc = await getDoc(partyRef);

    if (!partyDoc.exists()) {
      throw new Error(`Party ${partyId} not found`);
    }

    const party = partyDoc.data() as ListeningParty;

    // Remove member
    party.memberIds = party.memberIds.filter(id => id !== userId);
    delete party.members[userId];

    // If no members left or host left, deactivate party
    if (party.memberIds.length === 0 || party.hostUid === userId) {
      party.isActive = false;
    }

    await updateDoc(partyRef, {
      memberIds: party.memberIds,
      members: party.members,
      isActive: party.isActive,
    });
  } catch (error) {
    console.error('Failed to leave party:', error);
    throw error;
  }
}

/**
 * End a party (host only).
 */
export async function endParty(partyId: string, hostUid: string): Promise<void> {
  try {
    const partyRef = doc(db, 'listeningParties', partyId);
    const partyDoc = await getDoc(partyRef);

    if (!partyDoc.exists()) {
      throw new Error(`Party ${partyId} not found`);
    }

    const party = partyDoc.data() as ListeningParty;

    if (party.hostUid !== hostUid) {
      throw new Error('Only host can end party');
    }

    await updateDoc(partyRef, {
      isActive: false,
    });
  } catch (error) {
    console.error('Failed to end party:', error);
    throw error;
  }
}

/**
 * Set party playback state (any member can DJ).
 * Updates playback info and schedules the targetEmitServerTime.
 */
export async function setPartyPlayback(
  partyId: string,
  userId: string,
  playback: PartyPlayback
): Promise<void> {
  try {
    const partyRef = doc(db, 'listeningParties', partyId);
    const partyDoc = await getDoc(partyRef);

    if (!partyDoc.exists()) {
      throw new Error(`Party ${partyId} not found`);
    }

    const party = partyDoc.data() as ListeningParty;

    // Any member of the party can control playback (collaborative DJ)
    if (!party.memberIds.includes(userId)) {
      throw new Error('Only party members can control playback');
    }

    await updateDoc(partyRef, {
      playback,
    });
  } catch (error) {
    console.error('Failed to set playback:', error);
    throw error;
  }
}

/**
 * Subscribe to real-time party updates.
 * Returns an unsubscribe function.
 */
export function subscribeToParty(
  partyId: string,
  onUpdate: (party: ListeningParty) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const partyRef = doc(db, 'listeningParties', partyId);

  return onSnapshot(
    partyRef,
    snapshot => {
      if (snapshot.exists()) {
        const party = snapshot.data() as ListeningParty;
        onUpdate(party);
      } else {
        onError?.(new Error(`Party ${partyId} not found`));
      }
    },
    error => {
      console.error('Subscription error:', error);
      onError?.(error as Error);
    }
  );
}

/**
 * Get all parties for a user (where user is a member or host).
 */
export async function getUserParties(userId: string): Promise<ListeningParty[]> {
  try {
    const q = query(
      collection(db, 'listeningParties'),
      where('memberIds', 'array-contains', userId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as ListeningParty);
  } catch (error) {
    console.error('Failed to get user parties:', error);
    throw error;
  }
}

/**
 * Get a single party by ID.
 */
export async function getPartyById(partyId: string): Promise<ListeningParty | null> {
  try {
    const partyRef = doc(db, 'listeningParties', partyId);
    const partyDoc = await getDoc(partyRef);

    if (!partyDoc.exists()) {
      return null;
    }

    return partyDoc.data() as ListeningParty;
  } catch (error) {
    console.error('Failed to get party:', error);
    throw error;
  }
}

/**
 * Update a member's calibration data (latency, clock offset, sync drift).
 */
export async function updateMemberCalibration(
  partyId: string,
  userId: string,
  calibration: {
    audioLatencyMs?: number;
    clockOffsetMs?: number;
    syncDriftMs?: number;
  }
): Promise<void> {
  try {
    const partyRef = doc(db, 'listeningParties', partyId);
    const partyDoc = await getDoc(partyRef);

    if (!partyDoc.exists()) {
      throw new Error(`Party ${partyId} not found`);
    }

    const party = partyDoc.data() as ListeningParty;
    const member = party.members[userId];

    if (!member) {
      throw new Error(`Member ${userId} not found in party`);
    }

    // Update member data
    if (calibration.audioLatencyMs !== undefined) {
      member.audioLatencyMs = calibration.audioLatencyMs;
    }
    if (calibration.clockOffsetMs !== undefined) {
      member.clockOffsetMs = calibration.clockOffsetMs;
    }
    if (calibration.syncDriftMs !== undefined) {
      member.syncDriftMs = calibration.syncDriftMs;
    }
    member.lastSeen = Date.now();

    await updateDoc(partyRef, {
      [`members.${userId}`]: member,
    });
  } catch (error) {
    console.error('Failed to update member calibration:', error);
    throw error;
  }
}

/**
 * Trigger a sync pulse at a given server time.
 * All devices will play an 880Hz burst at that time for acoustic calibration.
 */
export async function triggerSyncPulse(
  partyId: string,
  hostUid: string,
  syncServerTime: number
): Promise<void> {
  try {
    const partyRef = doc(db, 'listeningParties', partyId);
    const partyDoc = await getDoc(partyRef);

    if (!partyDoc.exists()) {
      throw new Error(`Party ${partyId} not found`);
    }

    const party = partyDoc.data() as ListeningParty;

    if (party.hostUid !== hostUid) {
      throw new Error('Only host can trigger sync pulse');
    }

    await updateDoc(partyRef, {
      syncPulse: {
        at: syncServerTime,
        triggeredBy: hostUid,
      },
    });
  } catch (error) {
    console.error('Failed to trigger sync pulse:', error);
    throw error;
  }
}

/**
 * Clear the sync pulse after it has completed.
 */
export async function clearSyncPulse(partyId: string): Promise<void> {
  try {
    const partyRef = doc(db, 'listeningParties', partyId);
    await updateDoc(partyRef, {
      syncPulse: null,
    });
  } catch (error) {
    console.error('Failed to clear sync pulse:', error);
    throw error;
  }
}

// ─── Party Chat ─────────────────────────────────────────────────────────────

export interface PartyMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderImageUrl?: string;
  text: string;
  createdAt: Timestamp | string;
}

/**
 * Subscribe to real-time messages in the party chat subcollection.
 */
export function subscribePartyMessages(
  partyId: string,
  callback: (msgs: PartyMessage[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'listeningParties', partyId, 'messages'),
    orderBy('createdAt', 'asc'),
    limit(200),
  );
  return onSnapshot(q, (snap) => {
    const msgs = snap.docs.map((d) => ({ id: d.id, ...d.data() } as PartyMessage));
    callback(msgs);
  }, () => { /* ignore errors */ });
}

/**
 * Send a message to the party chat.
 */
export async function sendPartyMessage(
  partyId: string,
  senderId: string,
  senderName: string,
  text: string,
  senderImageUrl?: string,
): Promise<void> {
  await addDoc(collection(db, 'listeningParties', partyId, 'messages'), {
    senderId,
    senderName,
    senderImageUrl: senderImageUrl || '',
    text: text.trim(),
    createdAt: serverTimestamp(),
  });
}
